import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { LogOut, Menu } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_breathwork-elite/artifacts/sv3jydix_EGBW%20Logo%20100%20x%2084mm%20%281%29.png";

export default function AppNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const desktopLinkClass = ({ isActive }) =>
    `text-sm font-medium px-3 py-2 rounded-md transition-colors ${
      isActive ? "text-primary bg-secondary/40" : "text-muted-foreground hover:text-primary"
    }`;

  const mobileLinkClass = (path) =>
    `block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
      location.pathname === path
        ? "bg-secondary/60 text-primary"
        : "text-foreground hover:bg-muted"
    }`;

  const loggedInLinks = [
    { to: "/dashboard", label: "Dashboard", testid: "dashboard" },
    { to: "/my-plan", label: "My Plan", testid: "myplan" },
    { to: "/library", label: "Library", testid: "library" },
    { to: "/videos", label: "Videos", testid: "videos" },
    { to: "/progress", label: "Progress", testid: "progress" },
    { to: "/articles", label: "Articles", testid: "articles" },
    { to: "/book", label: "Book", testid: "book" },
  ];
  if (user?.role === "admin") loggedInLinks.push({ to: "/admin", label: "Admin", testid: "admin" });

  const loggedOutLinks = [
    { to: "/", label: "Home", testid: "home" },
    { to: "/articles", label: "Articles", testid: "articles" },
    { to: "/book", label: "Book", testid: "book" },
    { to: "/corporate", label: "Corporate", testid: "corporate" },
  ];

  const links = user ? loggedInLinks : loggedOutLinks;

  return (
    <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between gap-3">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 group shrink-0" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Evan Gill — Elite Performance Through Breath" className="h-11 sm:h-14 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={desktopLinkClass} data-testid={`nav-${l.testid}`}>{l.label}</NavLink>
          ))}
        </nav>

        {/* Desktop auth */}
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <span className="hidden lg:inline text-sm text-muted-foreground truncate max-w-[10rem]" data-testid="nav-user-name">{user.name}</span>
              <Button variant="ghost" size="sm" onClick={() => navigate("/change-password")} data-testid="nav-change-password-btn">Account</Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="nav-logout-btn">
                <LogOut className="h-4 w-4 mr-1" /> Logout
              </Button>
            </>
          ) : (
            <>
              <a
                href="https://evangill.xyz"
                className="hidden lg:inline text-sm text-muted-foreground hover:text-primary transition-colors mr-1"
                data-testid="nav-back-to-main-site"
              >
                ← evangill.xyz
              </a>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login-btn">Login</Button>
              <Button size="sm" className="rounded-full" onClick={() => navigate("/register")} data-testid="nav-register-btn">Join</Button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="nav-mobile-toggle">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col" data-testid="nav-mobile-panel">
            <div className="px-6 pt-6 pb-4 border-b border-border">
              <img src={LOGO_URL} alt="Evan Gill" className="h-10 w-auto" />
              {user && <p className="mt-3 text-sm text-muted-foreground truncate">{user.name}</p>}
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {links.map((l) => (
                <SheetClose asChild key={l.to}>
                  <Link to={l.to} className={mobileLinkClass(l.to)} data-testid={`nav-mobile-${l.testid}`}>{l.label}</Link>
                </SheetClose>
              ))}
            </nav>
            <div className="px-4 py-4 border-t border-border space-y-2">
              {user ? (
                <>
                  <SheetClose asChild>
                    <Button variant="outline" className="w-full rounded-full" onClick={() => navigate("/change-password")} data-testid="nav-mobile-account">Account</Button>
                  </SheetClose>
                  <Button variant="ghost" className="w-full rounded-full" onClick={handleLogout} data-testid="nav-mobile-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </Button>
                </>
              ) : (
                <>
                  <SheetClose asChild>
                    <Button className="w-full rounded-full" onClick={() => navigate("/register")} data-testid="nav-mobile-register">Join the Academy</Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button variant="outline" className="w-full rounded-full" onClick={() => navigate("/login")} data-testid="nav-mobile-login">Login</Button>
                  </SheetClose>
                  <a
                    href="https://evangill.xyz"
                    className="block text-center text-sm text-muted-foreground hover:text-primary transition-colors pt-2"
                    data-testid="nav-mobile-back-to-main-site"
                  >
                    ← evangill.xyz
                  </a>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
